import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Card,
  Row,
  Col,
  Statistic,
  Table,
  Progress,
  Tag,
  Descriptions,
  Segmented,
  Space,
} from 'antd';
import type { Dayjs } from 'dayjs';
import { adminApi } from '@services/api';
import {
  AnalyticsDateRangeFilter,
  type AnalyticsPeriod,
  type AnalyticsDateRange,
  type AnalyticsShownRange,
} from '@/components';
import { ExportButton } from '@components/ExportButton';
import { useLatestRequest } from '@hooks/useLatestRequest';
import { analyticsWindow } from '@utils/analyticsWindow';
import type { UserAiAnalyticsResponse, AiBreakdownItem, AiTeamCount } from '@/types';
import { WicketDiagnostics } from './WicketDiagnostics';
import {
  bucketBreakdownRows,
  parseBucketValue,
  DIFFICULTY_BUCKET_OPTIONS,
  type DifficultyBucketSize,
} from '@/utils/difficultyBucket';

interface Props {
  playerId: string;
}

const breakdownColumns = [
  { title: 'Category', dataIndex: 'label', key: 'label' },
  { title: 'Matches', dataIndex: 'matches', key: 'matches', width: 80 },
  { title: 'W', dataIndex: 'wins', key: 'wins', width: 50 },
  { title: 'L', dataIndex: 'losses', key: 'losses', width: 50 },
  { title: 'T', dataIndex: 'ties', key: 'ties', width: 50 },
  {
    title: 'Win Rate',
    dataIndex: 'winRate',
    key: 'winRate',
    width: 140,
    render: (val: number) => (
      <Progress
        percent={val}
        size="small"
        format={(p) => `${p?.toFixed(1)}%`}
        strokeColor={val >= 60 ? '#52c41a' : val >= 40 ? '#faad14' : '#ff4d4f'}
      />
    ),
  },
];

const teamColumns = [
  { title: 'Team', dataIndex: 'team', key: 'team' },
  { title: 'Matches', dataIndex: 'count', key: 'count', width: 80 },
];

export const PlayerAiAnalytics: React.FC<Props> = ({ playerId }) => {
  const [data, setData] = useState<UserAiAnalyticsResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [period, setPeriod] = useState('last_30_days');
  const [customRange, setCustomRange] = useState<[Dayjs, Dayjs] | null>(null);
  const [difficultyBucket, setDifficultyBucket] = useState<DifficultyBucketSize>('individual');
  // The range and fetch time of the figures on screen, for the date label.
  const [shown, setShown] = useState<AnalyticsShownRange | null>(null);
  const beginRequest = useLatestRequest();

  const bucketedByDifficulty = useMemo(
    () => (data ? bucketBreakdownRows(data.byDifficulty, difficultyBucket) : []),
    [data, difficultyBucket]
  );
  // The same bucketing applies to byAiGroup + byDivision. The helper is
  // a no-op on rows whose `value` is non-numeric (e.g. AI Group buckets
  // arrive as categorical strings), so it's safe to thread the toggle
  // through every breakdown panel — categorical ones simply pass
  // through untouched.
  const bucketedByAiGroup = useMemo(
    () => (data ? bucketBreakdownRows(data.byAiGroup, difficultyBucket) : []),
    [data, difficultyBucket]
  );
  const bucketedByDivision = useMemo(
    () => (data ? bucketBreakdownRows(data.byDivision, difficultyBucket) : []),
    [data, difficultyBucket]
  );

  // Wicket-diagnostics export, pre-bound to this player.
  const exportOptions = useMemo(
    () => [
      {
        key: 'ai-wicket-diagnostics-player',
        label: 'AI Wicket Diagnostics (this player)',
        exportFn: (fromDate?: string, toDate?: string) =>
          adminApi.exportAiWicketDiagnostics(fromDate, toDate, playerId),
        filename: `ai_wicket_diagnostics_${playerId.slice(0, 8)}`,
      },
    ],
    [playerId]
  );

  const fetchData = useCallback(async () => {
    const isLatest = beginRequest();
    setLoading(true);
    const startedAt = Date.now();
    const range: AnalyticsDateRange = {
      period: period as AnalyticsPeriod,
      customRange: customRange ?? undefined,
    };
    try {
      const params: Record<string, string> = {};
      if (period === 'custom' && customRange) {
        params.period = 'custom';
        params.startDate = customRange[0].format('YYYY-MM-DD');
        params.endDate = customRange[1].format('YYYY-MM-DD');
      } else {
        params.period = period;
      }
      const res = await adminApi.getUserAiAnalytics(playerId, params);
      if (!isLatest()) return;
      setData(res.data);
      setShown({ range, asOf: startedAt });
    } catch (err) {
      console.error('Failed to load AI analytics:', err);
    } finally {
      if (isLatest()) setLoading(false);
    }
  }, [playerId, period, customRange, beginRequest]);

  // The wicket panels query exactly the labelled window, and only refetch
  // when it changes (an inline object made them refetch on every render).
  const wicketFilters = useMemo(() => {
    const labelled = shown && analyticsWindow(shown.range, shown.asOf);
    return labelled
      ? {
          period: 'custom',
          startDate: labelled.start.toISOString(),
          endDate: labelled.end.toISOString(),
        }
      : undefined;
  }, [shown]);

  useEffect(() => {
    void fetchData();
  }, [fetchData]);

  const filterRow = (
    <Row justify="end">
      <Space wrap>
        <Space size={6}>
          <span style={{ fontSize: 12, color: '#8c8c8c' }}>Bucket</span>
          <Segmented
            size="small"
            value={String(difficultyBucket)}
            onChange={(v) => setDifficultyBucket(parseBucketValue(String(v)))}
            options={[...DIFFICULTY_BUCKET_OPTIONS]}
          />
        </Space>
        <AnalyticsDateRangeFilter
          value={{
            period: period as AnalyticsPeriod,
            customRange: customRange ?? undefined,
          }}
          shown={shown}
          onApply={(next: AnalyticsDateRange) => {
            setPeriod(next.period);
            setCustomRange(next.customRange ?? null);
          }}
        />
        <ExportButton options={exportOptions} buttonText="Export wickets" single />
      </Space>
    </Row>
  );

  if (loading && !data) {
    return <Card loading />;
  }

  if (!data || data.summary.totalMatches === 0) {
    // Keep the filter: without it a period with no matches can't be changed.
    return (
      <Space direction="vertical" size="middle" style={{ width: '100%' }}>
        {filterRow}
        <Card>No AI analytics data available for this period.</Card>
      </Space>
    );
  }

  const { summary, battingStats, bowlingStats, teamRatings, battingOrder, autoPlay } = data;

  return (
    <Space direction="vertical" size="middle" style={{ width: '100%' }}>
      {filterRow}

      {/* Summary Cards */}
      <Row gutter={[16, 16]}>
        <Col xs={12} sm={6}>
          <Card size="small">
            <Statistic title="Total Matches" value={summary.totalMatches} />
          </Card>
        </Col>
        <Col xs={12} sm={6}>
          <Card size="small">
            <Statistic
              title="Win Rate"
              value={summary.winRate}
              suffix="%"
              valueStyle={{ color: summary.winRate >= 50 ? '#3f8600' : '#cf1322' }}
            />
          </Card>
        </Col>
        <Col xs={12} sm={6}>
          <Card size="small">
            <Statistic title="Avg User RR" value={summary.avgUserRunRate} precision={2} />
          </Card>
        </Col>
        <Col xs={12} sm={6}>
          <Card size="small">
            <Statistic title="Avg AI RR" value={summary.avgAiRunRate} precision={2} />
          </Card>
        </Col>
      </Row>

      {/* W/L/T Summary */}
      <Row gutter={[16, 16]}>
        <Col xs={8}>
          <Card size="small">
            <Statistic title="Wins" value={summary.wins} valueStyle={{ color: '#3f8600' }} />
          </Card>
        </Col>
        <Col xs={8}>
          <Card size="small">
            <Statistic title="Losses" value={summary.losses} valueStyle={{ color: '#cf1322' }} />
          </Card>
        </Col>
        <Col xs={8}>
          <Card size="small">
            <Statistic title="Ties" value={summary.ties} valueStyle={{ color: '#faad14' }} />
          </Card>
        </Col>
      </Row>

      {/* Breakdown Tables — bucket controlled by the page-header selector */}
      <Row gutter={[16, 16]}>
        <Col xs={24} lg={12}>
          <Card title="By Difficulty" size="small">
            <Table<AiBreakdownItem>
              dataSource={bucketedByDifficulty}
              columns={breakdownColumns}
              rowKey="label"
              pagination={false}
              size="small"
              scroll={{ y: 320 }}
            />
          </Card>
        </Col>
        <Col xs={24} lg={12}>
          <Card title="By AI Group" size="small">
            <Table<AiBreakdownItem>
              dataSource={bucketedByAiGroup}
              columns={breakdownColumns}
              rowKey="label"
              pagination={false}
              size="small"
            />
          </Card>
        </Col>
      </Row>

      <Row gutter={[16, 16]}>
        <Col xs={24} lg={12}>
          <Card title="By Verdict" size="small">
            <Table
              dataSource={data.byVerdict}
              columns={[
                {
                  title: 'Verdict',
                  dataIndex: 'label',
                  key: 'label',
                  render: (label: string) => {
                    const colorMap: Record<string, string> = {
                      Won: 'green',
                      Lost: 'red',
                      Tie: 'orange',
                      Abandoned: 'default',
                    };
                    return <Tag color={colorMap[label] ?? 'default'}>{label}</Tag>;
                  },
                },
                { title: 'Matches', dataIndex: 'matches', key: 'matches' },
              ]}
              rowKey="label"
              pagination={false}
              size="small"
              scroll={{ y: 320 }}
            />
          </Card>
        </Col>
        <Col xs={24} lg={12}>
          <Card title="By Division" size="small">
            <Table<AiBreakdownItem>
              dataSource={bucketedByDivision}
              columns={breakdownColumns}
              rowKey="label"
              pagination={false}
              size="small"
              scroll={{ y: 320 }}
            />
          </Card>
        </Col>
      </Row>

      {/* Batting & Bowling Stats */}
      <Row gutter={[16, 16]}>
        <Col xs={24} lg={12}>
          <Card title="Batting Stats" size="small">
            <Descriptions column={2} size="small" bordered>
              <Descriptions.Item label="Total 4s">{battingStats.totalFours}</Descriptions.Item>
              <Descriptions.Item label="Total 6s">{battingStats.totalSixes}</Descriptions.Item>
              <Descriptions.Item label="Avg 4s/Match">
                {battingStats.avgFoursPerMatch}
              </Descriptions.Item>
              <Descriptions.Item label="Avg 6s/Match">
                {battingStats.avgSixesPerMatch}
              </Descriptions.Item>
              <Descriptions.Item label="Avg Dot Balls">
                {battingStats.avgUserDotBalls}
              </Descriptions.Item>
            </Descriptions>
          </Card>
        </Col>
        <Col xs={24} lg={12}>
          <Card title="Opponent Bowling Stats" size="small">
            <Descriptions column={2} size="small" bordered>
              <Descriptions.Item label="Avg AI Dot Balls">
                {bowlingStats.avgAiDotBalls}
              </Descriptions.Item>
              <Descriptions.Item label="Avg AI 4s">{bowlingStats.avgAiFours}</Descriptions.Item>
              <Descriptions.Item label="Avg AI 6s">{bowlingStats.avgAiSixes}</Descriptions.Item>
            </Descriptions>
          </Card>
        </Col>
      </Row>

      {/* Team Ratings */}
      <Card title="Team Ratings" size="small">
        <Row gutter={[16, 16]}>
          <Col xs={12}>
            <Statistic title="Avg PTR" value={teamRatings?.avgPTR ?? 0} precision={1} />
          </Col>
          <Col xs={12}>
            <Statistic title="Avg OTR" value={teamRatings?.avgOTR ?? 0} precision={1} />
          </Col>
        </Row>
      </Card>

      {/* Batting Order */}
      <Card title="Batting Order Analysis" size="small">
        <Row gutter={[16, 16]}>
          <Col xs={12}>
            <Statistic
              title="Bat First"
              value={battingOrder.batFirstMatches}
              suffix={
                <span style={{ fontSize: 14, color: '#888' }}>
                  ({battingOrder.batFirstWins}W - {battingOrder.batFirstWinRate}%)
                </span>
              }
            />
          </Col>
          <Col xs={12}>
            <Statistic
              title="Bowl First"
              value={battingOrder.bowlFirstMatches}
              suffix={
                <span style={{ fontSize: 14, color: '#888' }}>
                  ({battingOrder.bowlFirstWins}W - {battingOrder.bowlFirstWinRate}%)
                </span>
              }
            />
          </Col>
        </Row>
      </Card>

      {/* Team Preferences */}
      <Row gutter={[16, 16]}>
        <Col xs={24} lg={12}>
          <Card title="Most Used Teams" size="small">
            <Table<AiTeamCount>
              dataSource={data.topUserTeams}
              columns={teamColumns}
              rowKey="team"
              pagination={false}
              size="small"
              scroll={{ y: 320 }}
            />
          </Card>
        </Col>
        <Col xs={24} lg={12}>
          <Card title="Most Faced AI Teams" size="small">
            <Table<AiTeamCount>
              dataSource={data.topAiTeams}
              columns={teamColumns}
              rowKey="team"
              pagination={false}
              size="small"
              scroll={{ y: 320 }}
            />
          </Card>
        </Col>
      </Row>

      {/* Auto-play */}
      <Card title="Auto-Play Usage" size="small">
        <Row gutter={[16, 16]}>
          <Col xs={12}>
            <Statistic
              title="Auto-Play Batting"
              value={autoPlay.autoPlayBatPercent}
              suffix="%"
              precision={1}
            />
            <div style={{ fontSize: 12, color: '#888' }}>
              {autoPlay.autoPlayBatMatches} / {autoPlay.totalMatches} matches
            </div>
          </Col>
          <Col xs={12}>
            <Statistic
              title="Auto-Play Bowling"
              value={autoPlay.autoPlayBowlPercent}
              suffix="%"
              precision={1}
            />
            <div style={{ fontSize: 12, color: '#888' }}>
              {autoPlay.autoPlayBowlMatches} / {autoPlay.totalMatches} matches
            </div>
          </Col>
        </Row>
      </Card>

      {/* Wicket diagnostics scoped to this player */}
      <WicketDiagnostics userId={playerId} filters={wicketFilters ?? { period }} />
    </Space>
  );
};
